/**
 * Anonymous, per-browser, per-catalog "last score" store (TRAIN-05, D-07).
 *
 * Structurally independent of the legacy gamification engine in the sibling `practice`
 * frontend (its engagement-tracking managers persist to a browser database under an entirely
 * different name/mechanism). This store uses plain `window.localStorage` only — no
 * other browser storage API, no cross-package import — and keeps exactly one record
 * per organization+catalog (the last score, not a history or a best score).
 *
 * Both functions are fully synchronous so the catalog picker can read the last score
 * during render with no loading state (D-07).
 *
 * Key schema (FINAL, per 73-02-PLAN.md interfaces block) is built by the private
 * `key()` helper below from the `{organizationId}:{catalogId}` pair, prefixed once.
 *
 * The organizationId segment is load-bearing even though production is one federation
 * per subdomain: `OrganizationContext.tsx` resolves the org from a `?dev-org=` query
 * param / `referee:dev:org` localStorage key on localhost, so without the prefix,
 * local multi-federation testing would bleed scores between orgs.
 */
export interface TrainingStatsRecord {
  score: number;
  maxScore: number;
  percentage: number; // integer 0-100, pre-computed at write time
  completedAt: string; // ISO 8601
}

function key(organizationId: string, catalogId: string): string {
  return `referee:training:${organizationId}:${catalogId}`;
}

export function getLastScore(organizationId: string, catalogId: string): TrainingStatsRecord | null {
  try {
    const raw = localStorage.getItem(key(organizationId, catalogId));
    return raw ? (JSON.parse(raw) as TrainingStatsRecord) : null;
  } catch {
    // Storage disabled/unavailable (e.g. Safari private browsing) or the stored value
    // is hand-edited/corrupt JSON — degrade to "no stats" rather than throwing, since
    // this is a purely cosmetic display value with no downstream consumer that trusts it.
    return null;
  }
}

export function recordScore(organizationId: string, catalogId: string, score: number, maxScore: number): void {
  try {
    const percentage = maxScore === 0 ? 0 : Math.round((score / maxScore) * 100);
    localStorage.setItem(
      key(organizationId, catalogId),
      JSON.stringify({
        score,
        maxScore,
        percentage,
        completedAt: new Date().toISOString(),
      } satisfies TrainingStatsRecord),
    );
  } catch {
    // Safari private browsing sets the storage quota to 0, making setItem throw a
    // QuotaExceededError-shaped exception. Never let a storage failure block the exam
    // flow or crash the results screen — silently no-op instead.
  }
}
